/**
 * 새 조직 생성 모달. cloud-api `POST /organizations`.
 *
 * 디자인: 단순 backdrop + 카드 (Radix Dialog 미도입 — 의존성 최소화).
 * 외부 클릭 / Esc 로 닫힘. 폼 제출 성공 시 onCreated 콜백으로 부모가
 * 캐시 invalidate.
 *
 * Slug 자동 생성: 사용자가 name 입력 중에 slug 가 비어있다면 name 을 kebab
 * 으로 변환해 채움. 사용자가 slug 를 한 번이라도 손대면 자동 갱신 중지.
 */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  createOrganization,
  type OrganizationWithRole,
  type OrgPlan,
} from "@/lib/console-api";
import { extractErrorMessage } from "@/lib/api-client";

const SLUG_REGEX = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

function toSlug(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9-\s]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 64);
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (org: OrganizationWithRole) => void;
}

export function CreateOrganizationDialog({
  open,
  onClose,
  onCreated,
}: Props): JSX.Element | null {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [plan, setPlan] = useState<OrgPlan>("PERSONAL");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  // Esc 로 닫기
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    nameRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // 닫힐 때 입력값 초기화
  useEffect(() => {
    if (!open) {
      setName("");
      setSlug("");
      setSlugManuallyEdited(false);
      setPlan("PERSONAL");
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!SLUG_REGEX.test(slug)) {
      setError("slug 은 소문자/숫자/하이픈만 가능합니다 (예: my-team)");
      return;
    }
    setSubmitting(true);
    try {
      const created = await createOrganization({ name, slug, plan });
      onCreated(created);
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err, "조직 생성에 실패했습니다"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">새 조직 만들기</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              팀의 이름과 식별자를 정해 주세요.
            </p>
          </div>
          <button
            type="button"
            aria-label="닫기"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="org-name" className="text-sm font-medium">
              조직 이름
            </label>
            <Input
              id="org-name"
              ref={nameRef}
              type="text"
              required
              maxLength={128}
              className="mt-1.5"
              placeholder="예: Acme Team"
              value={name}
              onChange={(e) => {
                const v = e.target.value;
                setName(v);
                if (!slugManuallyEdited) setSlug(toSlug(v));
              }}
            />
          </div>
          <div>
            <label htmlFor="org-slug" className="text-sm font-medium">
              Slug{" "}
              <span className="text-muted-foreground">
                (URL · 소문자/숫자/하이픈)
              </span>
            </label>
            <Input
              id="org-slug"
              type="text"
              required
              minLength={2}
              maxLength={64}
              className="mt-1.5 font-mono"
              placeholder="acme-team"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugManuallyEdited(true);
              }}
            />
          </div>
          <div>
            <span className="text-sm font-medium">플랜</span>
            <div className="mt-1.5 space-y-2">
              <PlanOption
                value="PERSONAL"
                selected={plan}
                onSelect={setPlan}
                title="개인 (Personal)"
                description="혼자 사용하는 워크스페이스. 무료."
              />
              <PlanOption
                value="TEAM"
                selected={plan}
                onSelect={setPlan}
                title="팀 (Team)"
                badge="🎉 무료 베타"
                description="멤버 초대 / 협업. 정식 출시 시 유료 전환 예정."
              />
            </div>
          </div>
          {error && (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
            >
              취소
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              만들기
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PlanOption({
  value,
  selected,
  onSelect,
  title,
  description,
  badge,
}: {
  value: OrgPlan;
  selected: OrgPlan;
  onSelect: (v: OrgPlan) => void;
  title: string;
  description: string;
  badge?: string;
}): JSX.Element {
  const isSelected = selected === value;
  return (
    <label
      className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 text-sm transition-colors ${
        isSelected
          ? "border-primary bg-primary/5"
          : "border-border hover:bg-accent/50"
      }`}
    >
      <input
        type="radio"
        name="org-plan"
        value={value}
        checked={isSelected}
        onChange={() => onSelect(value)}
        className="mt-0.5"
      />
      <div className="flex-1">
        <div className="flex items-center gap-2 font-medium">
          {title}
          {badge && (
            <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-normal text-primary">
              {badge}
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      </div>
    </label>
  );
}
